import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import theme from '../../../../assets/theme';
import { useMetrics } from '../../../../hooks/useMetrics';
import {
  SubjectOption,
  subjectOptionMapper,
} from '../../../../utils/createLessonPageUtils';
import { Input } from '../../../_atoms/_Input';
import { Button } from '../../../_atoms/Button';
import { FormContainer } from '../AnyContentOrSkillTab';
import { FloatingSelect } from './FloatingSelect';

const NovelStudiesForm = ({
  lessonType,
  handleCreatePrompt,
  setErrorMessage,
  errorMessage,
}: {
  lessonType: {
    id: string;
    displayName: string;
    comingSoon?: boolean;
    hasMoreDetails?: boolean;
    dropdownOptions?: { value: string; displayName: string }[];
  };
  errorMessage: string | undefined;
  handleCreatePrompt: (prompt: string) => void;
  setErrorMessage: (message: string | undefined) => void;
}) => {
  const metrics = useMetrics();
  const initNovelFromLocalStorage = () => {
    const localLanguage =
      typeof window !== 'undefined'
        ? localStorage.getItem('@curipod_v1_createWithAI-novel')
        : '';

    const initialNovel = localLanguage ? JSON.parse(localLanguage) : '';

    return initialNovel;
  };
  const [novel, setNovel] = useState(initNovelFromLocalStorage());
  const [chapter, setChapter] = useState('');

  const findDropdownOptions = (lessonTypeId: string) => {
    return (
      subjectOptionMapper['ela']?.lessonTypes?.find(l => l.id === lessonTypeId)
        ?.dropdownOptions || []
    );
  };

  const [focus, setFocus] = useState(findDropdownOptions(lessonType.id)[0].value);
  const [otherFocus, setOtherFocus] = useState<string>();

  const handleFocusChange = (value: string | number | boolean) => {
    setErrorMessage(undefined);
    setFocus(value as string);
  };

  const formErrorText = useMemo(() => {
    if (!novel) {
      return `Don't forget to enter a novel`;
    }
    if (focus === 'other' && !otherFocus) {
      return `Don't forget to tell us your focus skill`;
    }
    return undefined;
  }, [novel, focus, otherFocus]);

  const handlePressNext = () => {
    if (formErrorText) {
      setErrorMessage(formErrorText);
      return;
    }

    const focusName =
      focus === 'other'
        ? otherFocus
        : findDropdownOptions(lessonType.id).find(o => o.value === focus)?.displayName;

    localStorage.setItem('@curipod_v1_createWithAI-novel', JSON.stringify(novel));

    handleCreatePrompt(
      `A ${lessonType.displayName} lesson on ${novel} ${
        chapter ? `chapter ${chapter} ` : ''
      }focusing on ${focusName}`,
    );

    metrics.logEvent('CreatePage.NextButtonClicked', {
      lessonType: lessonType.id,
      novel,
      chapter,
      focus,
      otherFocus,
      prompt: `A ${lessonType.displayName} lesson on ${novel} ${
        chapter ? `chapter ${chapter} ` : ''
      }focusing on ${focusName}`,
    });
  };

  return (
    <>
      <InputsWrapper style={{ width: '100%' }}>
        <Input
          inputStyle={{
            height: 42,
            boxShadow: theme.effects.floatingCreatorElementShadow,
            border: 'none',
          }}
          label="Title and Author"
          labelStyle={{ fontSize: 16, fontWeight: 600, color: theme.colors.black }}
          placeholder="The Giver by Lois Lowry"
          value={novel}
          onChange={e => {
            if (errorMessage) setErrorMessage(undefined);
            setNovel(e.target.value);
          }}
        />
        <Input
          inputStyle={{
            boxShadow: theme.effects.floatingCreatorElementShadow,
            border: 'none',
            height: 42,
          }}
          label="Chapter (optional)"
          labelStyle={{ fontSize: 16, fontWeight: 600, color: theme.colors.black }}
          placeholder="12"
          value={chapter}
          onChange={e => setChapter(e.target.value)}
        />
      </InputsWrapper>
      <FloatingSelect
        onSelect={handleFocusChange}
        options={findDropdownOptions(lessonType.id)}
        value={focus}
        label="Focus"
      />
      {focus === 'other' && (
        <Input
          inputStyle={{
            height: 42,
            boxShadow: theme.effects.floatingCreatorElementShadow,
            border: 'none',
          }}
          label="What would you like to focus on?"
          labelStyle={{ fontSize: 16, fontWeight: 600, color: theme.colors.black }}
          placeholder="Character Development"
          value={otherFocus}
          onChange={e => {
            setErrorMessage(undefined);
            setOtherFocus(e.target.value);
          }}
        />
      )}
      <GetLessonButton onClick={handlePressNext} $disabled={!!formErrorText}>
        Next
      </GetLessonButton>
    </>
  );
};

const SkillReleaseForm = ({
  lessonType,
  handleCreatePrompt,
  setErrorMessage,
  errorMessage,
}: {
  lessonType: SubjectOption;
  errorMessage: string | undefined;
  handleCreatePrompt: (prompt: string) => void;
  setErrorMessage: (message: string | undefined) => void;
}) => {
  const findDropdownOptions = (lessonTypeId: string) => {
    return (
      subjectOptionMapper['ela']?.lessonTypes?.find(l => l.id === lessonTypeId)
        ?.dropdownOptions || []
    );
  };

  const [focus, setFocus] = useState(findDropdownOptions(lessonType.id)[0].value);
  const [lessonTheme, setLessonTheme] = useState<string>();
  const [otherFocus, setOtherFocus] = useState<string>();
  const metrics = useMetrics();
  const handleFocusChange = (value: string | number | boolean) => {
    setErrorMessage(undefined);
    setFocus(value as string);
  };

  const focusName =
    focus === 'other'
      ? otherFocus
      : findDropdownOptions(lessonType.id).find(o => o.value === focus)?.displayName;

  const formErrorText = useMemo(() => {
    if (focus === 'other' && !otherFocus) {
      return `Don't forget to tell us your focus skill`;
    }
    return undefined;
  }, [focus, otherFocus]);

  const handlePressNext = () => {
    if (formErrorText) {
      setErrorMessage(formErrorText);
      return;
    }

    const lessonPrompt =
      focusName && lessonType.prompt
        ? `${lessonType.prompt.replace(/{{focusName}}/g, focusName)} ${
            lessonTheme ? `The theme throughout the lesson should be ${lessonTheme}.` : ''
          }`
        : '';

    handleCreatePrompt(lessonPrompt);

    metrics.logEvent('CreatePage.NextButtonClicked', {
      lessonType: lessonType.id,
      focus,
      otherFocus,
      prompt: lessonPrompt,
    });
  };

  return (
    <>
      <FloatingSelect
        onSelect={handleFocusChange}
        options={findDropdownOptions(lessonType.id)}
        value={focus}
        label="Focus Skill"
        placeholder=""
      />
      {focus === 'other' && (
        <Input
          inputStyle={{
            height: 42,
            boxShadow: theme.effects.floatingCreatorElementShadow,
            border: 'none',
          }}
          label="What would you like to focus on?"
          labelStyle={{ fontSize: 16, fontWeight: 600, color: theme.colors.black }}
          placeholder="Thesis statements"
          value={otherFocus}
          onChange={e => {
            if (errorMessage) setErrorMessage(undefined);
            setOtherFocus(e.target.value);
          }}
        />
      )}
      <Input
        tooltip={`Share what your students are excited about right now to create a lesson they'll really connect to!`}
        inputStyle={{
          height: 42,
          boxShadow: theme.effects.floatingCreatorElementShadow,
          border: 'none',
        }}
        label="Theme for Lesson Passages and Examples (optional)"
        labelStyle={{ fontSize: 16, fontWeight: 600, color: theme.colors.black }}
        placeholder="Book or movie, history or science topic, or anything students are interested in..."
        value={lessonTheme}
        onChange={e => {
          setLessonTheme(e.target.value);
        }}
      />

      <GetLessonButton onClick={handlePressNext} $disabled={false}>
        Next
      </GetLessonButton>
    </>
  );
};

const InputsWrapper = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr;
  gap: 20px;

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
  }
`;

const ELAExtraDetailsForm = ({
  lessonType,
  handleCreatePrompt,
}: {
  lessonType: SubjectOption;
  handleCreatePrompt: (prompt: string) => void;
}) => {
  const [errorMessage, setErrorMessage] = useState<string>();

  return (
    <>
      <FormContainer style={{ maxWidth: 900, width: '100%' }}>
        {lessonType.id === 'novelStudy' && (
          <NovelStudiesForm
            lessonType={lessonType}
            handleCreatePrompt={handleCreatePrompt}
            setErrorMessage={setErrorMessage}
            errorMessage={errorMessage}
          />
        )}
        {(lessonType.id === 'partsOfAnEssay' ||
          lessonType.id === 'writingSkills' ||
          lessonType.id === 'shortResponse' ||
          lessonType.id === 'readingStrategies') && (
          <SkillReleaseForm
            lessonType={lessonType}
            handleCreatePrompt={handleCreatePrompt}
            setErrorMessage={setErrorMessage}
            errorMessage={errorMessage}
          />
        )}
      </FormContainer>
      {errorMessage && (
        <p style={{ color: theme.colors.red, textAlign: 'center' }}>{errorMessage}</p>
      )}
    </>
  );
};

const GetLessonButton = styled(Button)<{ $disabled: boolean }>`
  color: ${theme.colors.white};
  background-color: ${theme.colors.orange};
  box-shadow: ${theme.effects.magicFloatingShadow};
  margin: 20px auto;

  ${props =>
    props.$disabled &&
    css`
      background-color: ${theme.colors.orangeDark};
      box-shadow: ${theme.effects.magicFloatingShadow};
      &:hover {
        background-color: ${theme.colors.orangeDark};
        box-shadow: ${theme.effects.magicFloatingShadow};
      }
    `}

  &:hover {
    background-color: ${theme.colors.orangeDark};
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
  }

  &:focus,
  &:active {
    background-color: ${theme.colors.orangeDark};
    box-shadow: ${theme.effects.defaultShadow};
  }
`;

export default ELAExtraDetailsForm;
